// How to enter a registration code on a Scout. Labels match the Scout's
// Device Status dialog (vortex-launcher DeviceStatusDialog.kt): a Scout that
// is not registered shows Register Device; a registered one shows a
// Re-register button that opens the same form. The shield (VerifiedUser in
// BottomStatusBar.kt) sits on the bottom bar at a side that differs between
// launcher versions, so the steps don't name a side.
//
// Laid out as the on-device design system's InstructionCard: inset ground,
// a 4px accent bar on the leading edge. The Scout's own controls are drawn as
// keys so a customer can match them to the screen.

import type { ComponentType, ReactNode, SVGProps } from 'react';
import { CheckCircleIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { HomeIcon } from '@heroicons/react/20/solid';
import { ShieldCheckIcon } from '@heroicons/react/16/solid';

export type ScoutStepsVariant = 'register' | 'reconnect';

/** A control on the Scout, drawn as a key. Inline-block, so its word (or, for
 * an icon-only key, a zero-width space) sets the baseline it sits on. */
function Key({ children, icon: Glyph }: { children?: ReactNode; icon?: ComponentType<SVGProps<SVGSVGElement>> }) {
  return (
    <span className="inline-block whitespace-nowrap rounded-md border border-slate-600 bg-slate-800 px-1.5 text-sm font-medium leading-6 text-white">
      {Glyph && <Glyph className={`inline-block h-3.5 w-3.5 align-[-0.15em] ${children ? 'mr-1' : ''}`} aria-hidden="true" />}
      {children ?? '\u200b'}
    </span>
  );
}

const open = (
  <>
    Press <Key icon={HomeIcon}>Home</Key> on the Scout, then tap the
    shield <Key icon={ShieldCheckIcon} /> in the bottom bar.
  </>
);

const STEPS: Record<ScoutStepsVariant, ReactNode[]> = {
  // Used when we can't tell whether the Scout was registered before (a new
  // device record can belong to a Scout that still holds an old registration).
  register: [
    open,
    <>If you see <Key>Re-register</Key>, tap it.</>,
    <>Enter the code in <Key>Registration Code</Key>, then tap <Key>Register</Key> (or <Key>Re-register</Key>).</>,
  ],
  reconnect: [
    open,
    <>Tap <Key>Re-register</Key>.</>,
    <>Enter the code in <Key>Registration Code</Key>, then tap <Key>Re-register</Key>.</>,
  ],
};

export function ScoutCodeSteps({ variant = 'register' }: { variant?: ScoutStepsVariant }) {
  return (
    <div className="rounded-[10px] border border-slate-700 border-l-4 border-l-cyan-500 bg-slate-900/50 px-4 py-4 sm:px-5">
      <p className="text-base font-semibold text-white">
        {variant === 'reconnect' ? 'To reconnect your Scout' : 'To register your Scout'}
      </p>
      <ol className="mt-3 space-y-3 text-sm leading-7 text-slate-200">
        {STEPS[variant].map((step, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/15 text-xs font-semibold leading-none text-cyan-400">
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>
      {/* Icons sit in the step numbers' column, so all the text shares one edge. */}
      <div className="mt-4 space-y-2 text-sm">
        <p className="flex gap-3 text-slate-300">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <CheckCircleIcon className="h-4 w-4 text-emerald-400" aria-hidden="true" />
          </span>
          <span>The Scout shows “Device registered successfully!” when it's done.</span>
        </p>
        <p className="flex gap-3 text-slate-400">
          <span className="flex h-5 w-5 shrink-0 items-center justify-center">
            <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
          </span>
          <span>On the Scout itself? Copy the code, then use the paste icon in Registration Code.</span>
        </p>
      </div>
    </div>
  );
}
